import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/useToast';
import { NotificationSettings as NotificationSettingsType, NotificationSettingsApi } from '@/services/api';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const NotificationSettings = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState<NotificationSettingsType>({
    post_notifications: {
      successful_posts: true,
      failed_posts: true,
      scheduled_posts: true
    },
    account_notifications: {
      platform_connection_changes: true,
      security_events: true
    },
    frequency: 'immediate'
  });
  const [useCustomEmail, setUseCustomEmail] = useState(false);
  const [customEmail, setCustomEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = async () => {
    try {
      setIsLoading(true);
      const data = await NotificationSettingsApi.getSettings();
      setSettings(data);
      if (data.email) {
        setUseCustomEmail(true);
        setCustomEmail(data.email);
      }
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch notification settings:', err);
      
      toast({
        title: 'Error',
        description: 'Failed to load notification settings',
        type: 'error'
      });
      setError('Failed to load settings. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // Auto-save settings function (replaces the manual save)
  const saveSettings = async (updatedSettings: NotificationSettingsType) => {
    try {
      setIsSaving(true);
      await NotificationSettingsApi.updateSettings(updatedSettings);
      setSettings(updatedSettings);
      setError(null);
      // Only show toast on errors, not on successful autosaves
    } catch (err: any) {
      console.error('Failed to save notification settings:', err);
      
      toast({
        title: 'Error',
        description: 'Failed to save notification preferences',
        type: 'error'
      });
      setError('Failed to save settings. Please try again later.');
    } finally {
      setIsSaving(false);
    }
  };

  // Enhanced toggle function with immediate save
  const toggleSetting = async (
    category: 'post_notifications' | 'account_notifications',
    setting: string,
    value: boolean
  ) => {
    // Update local state immediately for responsive UI
    const updatedSettings = {
      ...settings,
      [category]: {
        ...settings[category],
        [setting]: value
      }
    };
    
    setSettings(updatedSettings);
    
    // Save to backend
    await saveSettings(updatedSettings);
  };
  
  // Handle toggle of custom email
  const handleCustomEmailToggle = (checked: boolean) => {
    setUseCustomEmail(checked);
    
    // If turning off custom email, update settings immediately
    if (!checked) {
      const updatedSettings = {
        ...settings,
        email: undefined
      };
      setSettings(updatedSettings);
      saveSettings(updatedSettings);
    }
  };
  
  // Save custom email when the dedicated button is clicked
  const handleSaveEmail = async () => {
    // Validate email
    if (useCustomEmail && customEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(customEmail)) {
        toast({
          title: 'Validation Error',
          description: 'Please enter a valid email address',
          type: 'error'
        });
        setError('Please enter a valid email address');
        return;
      }
      
      // Prepare the full payload with the updated email
      const payload: NotificationSettingsType = {
        ...settings, // Spread the current settings
        email: customEmail // Update the email field
      };
      
      try {
        setIsSaving(true);
        await NotificationSettingsApi.updateSettings(payload);
        // Update local state to confirm save
        setSettings(payload); 
        setError(null);
        toast({
          title: "Success",
          description: "Notification email address saved.",
          type: "success"
        });
      } catch (err: any) {
        console.error('Failed to save notification email:', err);
        toast({
          title: 'Error',
          description: 'Failed to save email address',
          type: 'error'
        });
        setError('Failed to save email address. Please try again later.');
      } finally {
        setIsSaving(false);
      }
    } else if (useCustomEmail && !customEmail) {
        // If toggle is on but email is empty, maybe prompt user or just don't save?
        toast({ title: "Info", description: "Email field is empty.", type: "info" });
    }
    // If useCustomEmail is false, do nothing when save is clicked
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex justify-center items-center h-40">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <p>Loading notification settings...</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h2 className="text-xl font-semibold mb-6">Notification Preferences</h2>
      
      {error && (
        <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {isSaving && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-2 rounded mb-4 flex items-center">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Saving changes...
        </div>
      )}
      
      <div className="space-y-6">
        {/* Active Notifications */}
        <div>
          <h3 className="text-lg font-medium mb-4">Active Notifications</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="successful-posts">Post goes live</Label>
              <Switch 
                id="successful-posts"
                checked={settings.post_notifications.successful_posts}
                onCheckedChange={(value) => toggleSetting('post_notifications', 'successful_posts', value)}
              />
            </div>
          </div>
        </div>
        
        {/* Coming Soon Notifications */}
        <div>
          <h3 className="text-lg font-medium mb-2">Coming Soon</h3>
          <p className="text-sm text-gray-500 mb-4">These notification options will be available in a future update.</p>
          <div className="space-y-3 opacity-60">
            <div className="flex items-center justify-between">
              <Label htmlFor="failed-posts">Post fails to go live</Label>
              <Switch 
                id="failed-posts"
                checked={settings.post_notifications.failed_posts}
                onCheckedChange={() => {}}
                disabled
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="scheduled-posts">Scheduled posts</Label>
              <Switch 
                id="scheduled-posts"
                checked={settings.post_notifications.scheduled_posts}
                onCheckedChange={() => {}}
                disabled
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="platform-connection">Platform connection changes</Label>
              <Switch 
                id="platform-connection"
                checked={settings.account_notifications.platform_connection_changes}
                onCheckedChange={() => {}}
                disabled
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="security-events">Security events</Label>
              <Switch 
                id="security-events"
                checked={settings.account_notifications.security_events}
                onCheckedChange={() => {}}
                disabled
              />
            </div>
          </div>
        </div>
        
        {/* Notification Frequency */}
        <div className="opacity-60">
          <h3 className="text-lg font-medium mb-2">Notification Frequency</h3>
          <p className="text-sm text-gray-500 mb-4">This feature will be available in a future update.</p>
          <div className="space-y-2">
            <Label htmlFor="frequency">How often would you like to receive notifications?</Label>
            <Select 
              value={settings.frequency} 
              onValueChange={() => {}}
              disabled
            >
              <SelectTrigger id="frequency" className="w-full">
                <SelectValue placeholder="Select frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="immediate">Immediate</SelectItem>
                <SelectItem value="daily">Daily digest</SelectItem>
                <SelectItem value="weekly">Weekly summary</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Custom Email Setting */}
        <div>
          <h3 className="text-lg font-medium mb-4">Notification Email</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="custom-email-toggle">Use custom email for notifications</Label>
              <Switch 
                id="custom-email-toggle"
                checked={useCustomEmail}
                onCheckedChange={handleCustomEmailToggle}
              />
            </div>
            
            {useCustomEmail && (
              <div className="space-y-2">
                <Label htmlFor="custom-email">Email address</Label>
                <Input 
                  id="custom-email"
                  name="custom-email"
                  type="email"
                  value={customEmail}
                  onChange={(e) => setCustomEmail(e.target.value)}
                  placeholder="Enter your email address"
                  autoComplete="off"
                />
              </div>
            )}
            
            {/* Add Save Button */} 
            {useCustomEmail && (
               <Button 
                  onClick={handleSaveEmail}
                  disabled={isSaving || !customEmail}
                  className="mt-2"
                  size="sm"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                     "Save Email"
                  )}
                </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}; 